import React, { ChangeEvent, useState } from "react";
import {
  TextInput,
  NumberInput,
  SelectInput,
  ArrayInput,
  SimpleFormIterator,
  required,
  RaRecord,
  InputProps,
} from "react-admin";
import {
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  Stack,
} from "@mui/material";
import { useWatch, useFormContext } from "react-hook-form";

import CurrencyInput from "../common/CurrencyInput";
import { ServiceResources } from "../types";

const ServiceResourcesInput = (
  props: Omit<InputProps<ServiceResources>, "source">
) => {
  // Use setValue and watch instead of useController, since using field names on multiple levels confuses react-hook-form ("resources" vs. "resources.domain.tldCounts")
  const { setValue } = useFormContext();
  const resources = useWatch({ name: "resources" });

  // Store enabled resources groups in state with initial value from current resources definition (for editing existing records)
  const [enabledGroups, setEnabledGroups] = useState({
    mail: Boolean(resources?.mail?.storage?.size),
    traffic: Boolean(resources?.traffic?.size),
    vhost: Boolean(resources?.vhost?.type),
    domain: Boolean(resources?.domain?.tldCounts?.length),
    vm: Boolean(resources?.vm?.memory),
  });

  // Change resources group state and set group value to null (to trigger changes for saving)
  const changeEnabledGroup =
    (group: string) => (evt: ChangeEvent<HTMLInputElement>) => {
      const checked = evt.target.checked;
      setEnabledGroups({
        ...enabledGroups,
        [group]: checked,
      });
      if (!checked) {
        setValue("resources", {
          ...resources,
          [group]: null,
        });
      }
    };

  return (
    <>
      <Divider style={{ marginTop: "1em", marginBottom: "1em" }} />

      <Typography variant="subtitle1" gutterBottom>
        Leistungsdefinition
      </Typography>

      <div>
        <Typography variant="h6">Mail</Typography>
        <FormControlLabel
          control={
            <Switch
              checked={enabledGroups.mail}
              onChange={changeEnabledGroup("mail")}
            />
          }
          label="Postfächer bereitstellen"
        />
        {enabledGroups.mail && (
          <Stack>
            <TextInput
              source="resources.mail.storage.size"
              label="Postfachspeicher"
              placeholder="z.B. 5GB"
              validate={required()}
            />
            <TextInput
              source="resources.mail.storage.exceedingSize"
              label="zus. Speicher Größe"
              placeholder="z.B. 10GB"
              validate={required()}
            />
            <CurrencyInput
              source="resources.mail.storage.exceedingCost"
              label="zus. Speicher Preis"
              placeholder="z.B. 15"
              validate={required()}
            />
          </Stack>
        )}
      </div>

      <Divider style={{ marginBottom: "1em" }} />

      <div>
        <Typography variant="h6">Traffic</Typography>

        <FormControlLabel
          control={
            <Switch
              checked={enabledGroups.traffic}
              onChange={changeEnabledGroup("traffic")}
            />
          }
          label="Traffic bereitstellen"
        />
        {enabledGroups.traffic && (
          <Stack>
            <TextInput
              source="resources.traffic.size"
              label="Volumen inkl."
              placeholder="z.B. 100GB"
              validate={required()}
            />
            <TextInput
              source="resources.traffic.exceedingSize"
              label="zus. Traffic Volumen"
              placeholder="z.B. 1GB"
              validate={required()}
            />
            <CurrencyInput
              source="resources.traffic.exceedingCost"
              label="zus. Traffic Preis"
              placeholder="z.B. 0.20"
              validate={required()}
            />
          </Stack>
        )}
      </div>

      <Divider style={{ marginBottom: "1em" }} />

      <div>
        <Typography variant="h6">VHost</Typography>

        <FormControlLabel
          control={
            <Switch
              checked={enabledGroups.vhost}
              onChange={changeEnabledGroup("vhost")}
            />
          }
          label="VHost bereitstellen"
        />
        {enabledGroups.vhost && (
          <Stack>
            <SelectInput
              source="resources.vhost.type"
              label="Art"
              choices={[
                { id: "Neos", name: "Neos" },
                { id: "Php", name: "Php" },
                { id: "Shopware", name: "Shopware" },
                { id: "Typo3", name: "Typo3" },
              ]}
              validate={required()}
            />
            <TextInput
              source="resources.vhost.storage.size"
              label="Speicherplatz inkl."
              placeholder="z.B. 5GB"
              validate={required()}
            />
            <TextInput
              source="resources.vhost.storage.exceedingSize"
              label="zus. Speicherplatz"
              placeholder="z.B. 10GB"
              validate={required()}
            />
            <CurrencyInput
              source="resources.vhost.storage.exceedingCost"
              label="zus. Speicherplatz Preis"
              placeholder="z.B. 15"
              validate={required()}
            />
          </Stack>
        )}
      </div>

      <Divider style={{ marginBottom: "1em" }} />

      <div>
        <Typography variant="h6">VM</Typography>

        <FormControlLabel
          control={
            <Switch
              checked={enabledGroups.vm}
              onChange={changeEnabledGroup("vm")}
            />
          }
          label="VM bereitstellen"
        />
        {enabledGroups.vm && (
          <Stack>
            <TextInput
              source="resources.vm.memory.size"
              label="RAM"
              placeholder="z.B. 4GB"
              validate={required()}
            />
            <TextInput
              source="resources.vm.memory.exceedingSize"
              label="zus. RAM"
              placeholder="z.B. 1GB"
              validate={required()}
              helperText="0 falls nicht definiert"
            />
            <CurrencyInput
              source="resources.vm.memory.exceedingCost"
              label="zus. RAM Preis"
              placeholder="z.B. 20"
              validate={required()}
            />
            <NumberInput
              source="resources.vm.vcpus.count"
              label="vCPUs"
              placeholder="z.B. 4"
              validate={required()}
            />
            <TextInput
              source="resources.vm.vcpus.exceedingCount"
              label="zus. vCPU"
              placeholder="z.B. 1"
              validate={required()}
              helperText="0 falls nicht definiert"
            />
            <CurrencyInput
              source="resources.vm.vcpus.exceedingCost"
              label="zus. vCPU Preis"
              placeholder="z.B. 20"
              validate={required()}
            />
            <TextInput
              source="resources.vm.storage.size"
              label="Speicherplatz"
              placeholder="z.B. 5GB"
              validate={required()}
            />
            <TextInput
              source="resources.vm.storage.exceedingSize"
              label="zus. Speicherplatz"
              placeholder="z.B. 10GB"
              validate={required()}
              helperText="0 falls nicht definiert"
            />
            <CurrencyInput
              source="resources.vm.storage.exceedingCost"
              label="zus. Speicherplatz Preis"
              placeholder="z.B. 10"
              validate={required()}
            />
          </Stack>
        )}
      </div>

      <Divider style={{ marginBottom: "1em" }} />

      <div>
        <Typography variant="h6">Domains</Typography>

        <FormControlLabel
          control={
            <Switch
              checked={enabledGroups.domain}
              onChange={changeEnabledGroup("domain")}
            />
          }
          label="Domains registrieren"
        />
        {enabledGroups.domain && (
          <div>
            <ArrayInput
              source="resources.domain.tldCounts"
              label="Anzahl pro TLD"
            >
              <SimpleFormIterator>
                <TextInput
                  source="tld"
                  label="Top-Level Domain"
                  placeholder="z.B. .de, .com, ..."
                  validate={required()}
                />
                <NumberInput
                  source="count"
                  label="Anzahl"
                  validate={required()}
                />
              </SimpleFormIterator>
            </ArrayInput>
          </div>
        )}
      </div>
    </>
  );
};

export const sanitizeServiceResources = (values: RaRecord): RaRecord => {
  if (values.resources?.domain?.tldCounts) {
    delete values.resources.domain.tldCountsIds;
  }
  if (values.resources) {
    removeTypenamePropertyRecursive(values.resources);
  }
  return values;
};

function removeTypenamePropertyRecursive(obj: any) {
  if (obj === null || typeof obj !== "object") {
    return;
  }

  if (Array.isArray(obj)) {
    obj.forEach((item) => {
      removeTypenamePropertyRecursive(item);
    });
    return;
  }

  if (obj?.__typename) {
    delete obj.__typename;
  }
  Object.keys(obj).forEach((key) => {
    if (typeof obj[key] === "object") {
      removeTypenamePropertyRecursive(obj[key]);
    }
  });
}

export default ServiceResourcesInput;
